import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from "@angular/core";
import CustomSwipe from "@vision/core-map-api/src/controls/CustomSwipe";
import { ProjectService } from "../../../services/internal-services/project.service";
import { IProjectAOI } from "../../../interfaces/i-project-aoi";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { MapService } from "../../../services/internal-services/map.service";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { ProjectStatus } from "../../../model/project-status";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../../model/feature-flags";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";
import { ToastService } from "../../../services/internal-services/toast.service";
import { INTERNAL_TOAST_TYPES, TOAST_STATUS } from "../../../model/ToastConstants";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-multilayer-swipe-viewer",
  templateUrl: "./multilayer-swipe-viewer.component.html",
  styleUrls: ["../swipe-viewer.component.scss"],
  standalone: false,
})
export class MultilayerSwipeViewerComponent implements OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  FeatureFlags = FeatureFlags;

  active = false;

  private _rightAOI: IProjectAOI = null;

  private _leftAOI: IProjectAOI = null;

  private _discreteEnabled: boolean = false;

  private _disable: boolean = true;

  private _dualView: boolean = false;

  private _geotiffStatus: boolean = false;

  enableByZoom: boolean = false;

  hasProjects: boolean = false;

  disableBySearch: boolean = false;

  swipeControl: CustomSwipe;

  @Output() visibleEvent = new EventEmitter<boolean>();

  @Output() sbsRightLayer = new EventEmitter<IProjectAOI>();

  @Output() sbsLeftLayer = new EventEmitter<IProjectAOI>();

  @Input()
  set dualView(dualView: boolean) {
    this._dualView = dualView;
    this.disable = dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
  }

  @Input()
  set discreteEnabled(discrete: boolean) {
    this._discreteEnabled = discrete;
    if (discrete && this.active) {
      this.disableSwipe();
    }
  }

  @Input()
  set geotiffStatus(geotiffStatus: boolean) {
    this._geotiffStatus = geotiffStatus;
    this.disable =
      this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
  }

  @Input()
  set projectStatus(status: ProjectStatus) {
    this.hasProjects = !!status?.enabledAOIs?.length;
    this.disable =
      this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
  }

  @Input()
  set zoomEvent(evt: any) {
    if (evt) {
      this.enableByZoom = evt.zoom >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL;
      this.disable =
        this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
    }
  }

  //TODO:THIS HAS TO BE WELL IMPLEMENTED BY NOT CLOSING THE TOOL WHEN SEARCHING
  @Input()
  set searchEvent(evt: any) {
    if (evt) {
      this.disableBySearch = evt.search;
      this.disable =
        this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
    }
  }

  @Input()
  set status(status: any) {
    if (status?.enable) {
      this.activeSwipe();
      this.leftAOI = status.left;
      this.rightAOI = status.right;
    }
  }

  set disable(disable: boolean) {
    if (disable !== this._disable) {
      this._disable = disable;
      if (disable) {
        this.disableSwipe();
      }
    }
  }

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected flagService: FeatureFlagsService,
    protected cdr: ChangeDetectorRef,
    protected toastService: ToastService,
  ) {}

  ngOnDestroy() {
    if (this.swipeControl) {
      this.disableSwipe();
    }
  }

  @Input()
  set leftAOI(aoi: IProjectAOI) {
    if (aoi && this.leftAOI?.getLayerId() !== aoi.getLayerId()) {
      this._leftAOI = aoi;
      this.updateLayer(aoi.getName(), false);
      this.sbsLeftLayer.emit(aoi);
      this.mapService.getOverlayManager().triggerMapStatus();
    }
  }

  @Input()
  set rightAOI(aoi: IProjectAOI) {
    if (aoi && this.rightAOI?.getLayerId() !== aoi.getLayerId()) {
      this._rightAOI = aoi;
      this.updateLayer(aoi.getName(), true);
      this.sbsRightLayer.emit(aoi);
    }
  }

  updateLayer(aoi, right?: boolean) {
    if (!this.swipeControl) {
      return;
    }

    if (right) {
      this.swipeControl.layers[1].layer.setCollection(aoi);
    } else {
      this.swipeControl.layers[0].layer.setCollection(aoi);
    }

    const status = {
      enable: this.active,
      getAOIAtPoint: this.getAOIAtPoint.bind(this),
      left: this.leftAOI,
      right: this.rightAOI,
    };

    this.updateStatus(status);
  }

  getAOIAtPoint(point) {
    const sideForPoint = this.swipeControl?.getSide(point);

    return sideForPoint === "left" ? this.leftAOI : this.rightAOI;
  }

  toggleSwipe() {
    if (this.active) {
      this.disableSwipe();
    } else {
      this.activeSwipe();
    }
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.BEFORE_AFTER_TOOL,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      this.active,
    );
  }

  activeSwipe() {
    this.viewerService.resetTimelineFilters();

    this.active = true;
    this.visibleEvent.emit(this.active);
    const map = this.mapService.getCoreMap();
    const layers = this.mapService
      .getOverlayManager()
      .getLayers()
      .filter((layer) => layer.values_.sourceName === ViewerConstants.LAYER_TYPE.MULTILAYER);
    this.swipeControl = new CustomSwipe({
      map,
      layers,
    });
    const centerAOI = this.viewerService.getProjectStatus().centerAOI;

    const visibleProjects = ProjectService.visibleProjects(this.viewerService.getProjectStatus().enabledAOIs);
    const indexRightAOI = visibleProjects.indexOf(centerAOI);

    const rightAOI =
      indexRightAOI === visibleProjects.length - 1
        ? visibleProjects[indexRightAOI - 1]
        : visibleProjects[visibleProjects.length - 1];

    this.rightAOI = rightAOI;
    this.leftAOI = centerAOI;

    this.viewerService.enableContextMenu();
  }

  disableSwipe() {
    this.active = false;
    this.visibleEvent.emit(this.active);

    const projectStatus = this.viewerService.getProjectStatus();
    if (projectStatus && this.leftAOI) {
      this.viewerService.updateProjectStatus(
        projectStatus.aois,
        this.leftAOI?.getName(),
        this.mapService.getMapStatus(),
      );
    }

    if (this.swipeControl) {
      this._leftAOI = null;
      this._rightAOI = null;
      this.swipeControl.dispatch(null);
      this.swipeControl = null;
    }
    const status = {
      enable: this.active,
      getAOIAtPoint: null,
    };
    this.disableBySearch = false;
    this.updateStatus(status);

    this.viewerService.enableContextMenu();
    this.viewerService.updateCenterImageDate();
    this.viewerService.updateUrlParameters(true);
    this.cdr.detectChanges();
  }

  updateStatus(status: any) {
    this.mapService.swipeStatus = status;
    this.mapService.getMapStatus().swipeStatus = status;
  }

  get leftAOI(): IProjectAOI {
    return this._leftAOI;
  }

  get rightAOI(): IProjectAOI {
    return this._rightAOI;
  }

  get disable(): boolean {
    return this._disable;
  }

  get dualView(): boolean {
    return this._dualView;
  }

  get discreteEnabled(): boolean {
    return this._discreteEnabled;
  }

  get geotiffStatus(): boolean {
    return this._geotiffStatus;
  }

  getTooltip() {
    if (this.dualView) {
      return "Dual-view is not compatible with the slider";
    }
    if (!this.viewerService.getProjectStatus()?.enabledAOIs?.length) {
      return "Before/After is not available in this area";
    }
    if (!this.viewerService.show2DTimeline()) {
      return "Zoom in to enable Before/After";
    }
    if (this.geotiffStatus) {
      return "Before/After is not available when Export GeoTIFF tool is open";
    }

    return "Before/After";
  }
}
